import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import { MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import {
  MODEL_RUNS_TABLE_FILTERS,
  MODEL_RUNS_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import { SortDirection } from '@elementary/common-ui/components/Table/types';
import {
  mapUrlFiltersToGraphqlFilters,
  parseGraphqlFilterOptions,
} from '@elementary/common-ui/utils/graphqlFilters';
import { getModelRunsAssetsFilterOptionsQuery } from '../api';
import {
  COLUMN_TO_FIELD_MAP,
  FIELD_TO_COLUMN_MAP,
  FILTERABLE_COLUMN_TO_FIELD_MAP,
  MODEL_RUNS_TABLE_TYPES,
} from '../consts';
import { ModelRunsAssetsFilterOptionsResponse } from '../types';

type UrlSort = { id: string; direction: SortDirection };

const DEFAULT_SORT: UrlSort = {
  id: 'last_exec_time',
  direction: 'desc',
};

const DIRECTION_TO_GQL: Record<SortDirection, 'ASC' | 'DESC'> = {
  asc: 'ASC',
  desc: 'DESC',
};

type Args = {
  search?: string;
};

const useModelRunsQueryContext = ({ search }: Args) => {
  const selectedEnvId = useSelectedEnv();
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();
  const [searchParams] = useSearchParams();

  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: MODEL_RUNS_TABLE_FILTERS,
  });

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const selectedTreeNodeIdsHash = useMemo(
    () => (selectedTreeNodeIds ? hashArray(selectedTreeNodeIds) : null),
    [selectedTreeNodeIds],
  );

  const urlSort: UrlSort = useMemo(
    () =>
      JSON.parse(searchParams.get(MODEL_RUNS_TABLE_SORT) || 'null') ||
      DEFAULT_SORT,
    [searchParams],
  );

  const assetFilters = useMemo(
    () =>
      mapUrlFiltersToGraphqlFilters(
        urlFiltersDebounced,
        FILTERABLE_COLUMN_TO_FIELD_MAP,
      ),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    const field = COLUMN_TO_FIELD_MAP[urlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: DIRECTION_TO_GQL[urlSort.direction],
    };
  }, [urlSort]);

  const hasEmptyTreeSelection = selectedTreeNodeIds?.length === 0;
  const ids = selectedTreeNodeIds?.length ? selectedTreeNodeIds : null;
  const trimmedSearch = search?.trim() || null;

  const filters = useMemo(
    () => ({
      tableTypes: MODEL_RUNS_TABLE_TYPES,
      ids,
      search: trimmedSearch,
    }),
    [ids, trimmedSearch],
  );

  const { data: filterOptionsData, isFetching: isFilterOptionsFetching } =
    useGraphqlQuery<ModelRunsAssetsFilterOptionsResponse>({
      query: getModelRunsAssetsFilterOptionsQuery,
      queryKey: [
        MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS,
        selectedEnvId,
        selectedTreeNodeIdsHash,
      ],
      variables: {
        filters: { ids, tableTypes: MODEL_RUNS_TABLE_TYPES },
      },
      enabled: !hasEmptyTreeSelection,
      envId: selectedEnvId,
    });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions(
        filterOptionsData?.tableAssetsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  return {
    selectedEnvId,
    selectedTreeNodeIdsHash,
    hasEmptyTreeSelection,
    urlFiltersDebounced,
    urlSort,
    sort,
    filters,
    assetFilters,
    filterOptions,
    isFilterOptionsFetching,
  };
};

export default useModelRunsQueryContext;
